import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getCourses, 
  getCourse as getCourseFirestore, 
  createCourse as createCourseFirestore, 
  updateCourse as updateCourseFirestore, 
  deleteCourse as deleteCourseFirestore 
} from '../firebase/unifiedDataService';
import { useUnifiedAuth } from './UnifiedAuthContext';
import { Logger } from '../utils/logger';

const log = Logger.create('CourseContext');

const CourseContext = createContext(null);

export function CourseProvider({ children }) {
  const { user } = useUnifiedAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load courses when user changes
  useEffect(() => {
    if (user) {
      loadCourses();
    } else {
      setCourses([]);
    }
  }, [user]);

  // Load all courses for the current user from Firestore (single source of truth)
  const loadCourses = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Firestore is the single source of truth for courses
      const coursesArray = await getCourses(user.uid);

      // Sort by createdAt (newest first), with archived at the end
      coursesArray.sort((a, b) => {
        if (a.archived && !b.archived) return 1;
        if (!a.archived && b.archived) return -1;
        return (b.createdAt || 0) - (a.createdAt || 0);
      });

      setCourses(coursesArray);
    } catch (err) {
      log.error('Error loading courses:', err);
      setError('Fehler beim Laden der Kurse');
    } finally {
      setLoading(false);
    }
  };

  // Get templates by course
  const getCoursesByCourse = (courseId) => {
    if (!courseId) {
      return courses.filter(c => !c.courseId);
    }
    return courses.filter(c => c.courseId === courseId);
  };

  // Create a new course in Firestore
  const createCourse = async ({ name, description = '', color = '#3B82F6', icon = '📚' }) => {
    if (!user) throw new Error('Not authenticated');

    try {
      const courseData = {
        name,
        description,
        color,
        icon,
        owner: user.uid,
        archived: false
      };

      const courseId = await createCourseFirestore(courseData);

      // Add to local state
      const newCourse = { id: courseId, ...courseData, createdAt: Date.now() };
      setCourses(prev => [newCourse, ...prev]);

      return newCourse;
    } catch (err) {
      log.error('Error creating course:', err);
      throw new Error('Fehler beim Erstellen des Kurses');
    }
  };

  // Update an existing course in Firestore
  const updateCourse = async (courseId, updates) => {
    if (!user) throw new Error('Not authenticated');

    try {
      await updateCourseFirestore(courseId, updates);

      // Update local state
      setCourses(prev =>
        prev.map(course =>
          course.id === courseId ? { ...course, ...updates, lastModified: Date.now() } : course
        )
      );
    } catch (err) {
      log.error('Error updating course:', err);
      throw new Error('Fehler beim Aktualisieren des Kurses');
    }
  };

  // Delete a course from Firestore
  const deleteCourse = async (courseId) => {
    if (!user) throw new Error('Not authenticated');

    try {
      await deleteCourseFirestore(courseId);

      // Remove from local state
      setCourses(prev => prev.filter(course => course.id !== courseId));
    } catch (err) {
      log.error('Error deleting course:', err);
      throw new Error('Fehler beim Löschen des Kurses');
    }
  };

  // Archive/unarchive a course
  const toggleArchiveCourse = async (courseId) => {
    const course = courses.find(c => c.id === courseId);
    if (!course) return;

    await updateCourse(courseId, { archived: !course.archived });
  };

  // Get a single course by ID from Firestore
  const getCourse = async (courseId) => {
    if (!user) throw new Error('Not authenticated');

    try {
      // Check local state first
      const localCourse = courses.find(c => c.id === courseId);
      if (localCourse) return localCourse;

      // If not in local state, fetch from Firestore
      const course = await getCourseFirestore(courseId);
      return course;
    } catch (err) {
      log.error('Error getting course:', err);
      throw new Error('Fehler beim Laden des Kurses');
    }
  };

  const value = {
    courses,
    loading,
    error,
    loadCourses,
    createCourse,
    updateCourse,
    deleteCourse,
    toggleArchiveCourse,
    getCourse
  };

  return (
    <CourseContext.Provider value={value}>
      {children}
    </CourseContext.Provider>
  );
}

// Custom hook to use course context
export function useCourses() {
  const context = useContext(CourseContext);
  if (!context) {
    throw new Error('useCourses must be used within a CourseProvider');
  }
  return context;
}

export default CourseContext;
